import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import {
  ProtectedRoute,
  RoleBasedRoute,
} from './components/auth/RoleBasedRoute';
import { FEATURE_FLAGS } from './config/featureFlags';

const AdminLayout = lazy(() =>
  import('./components/layout/AdminLayout').then(module => ({
    default: module.AdminLayout,
  }))
);
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then(module => ({
    default: module.DashboardPage,
  }))
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then(module => ({
    default: module.NotFoundPage,
  }))
);
const AuditLogsPage = lazy(() =>
  import('./pages/AuditLogsPage').then(module => ({
    default: module.AuditLogsPage,
  }))
);
const ProductsPage = lazy(() =>
  import('./pages/ProductsPage').then(module => ({
    default: module.ProductsPage,
  }))
);
const PlansPage = lazy(() =>
  import('./pages/PlansPage').then(module => ({
    default: module.PlansPage,
  }))
);
const FeaturesPage = lazy(() =>
  import('./pages/FeaturesPage').then(module => ({
    default: module.FeaturesPage,
  }))
);
const PlanEntitlementsPage = lazy(() =>
  import('./pages/PlanEntitlementsPage').then(module => ({
    default: module.PlanEntitlementsPage,
  }))
);
const AiUsageLimitsPage = lazy(() =>
  import('./pages/AiUsageLimitsPage').then(module => ({
    default: module.AiUsageLimitsPage,
  }))
);
const AiObservabilityPage = lazy(() =>
  import('./pages/AiObservabilityPage').then(module => ({
    default: module.AiObservabilityPage,
  }))
);
const CurrencyManagementPage = lazy(() =>
  import('./pages/CurrencyManagementPage').then(module => ({
    default: module.CurrencyManagementPage,
  }))
);
const BillingConfigPage = lazy(() =>
  import('./pages/BillingConfigPage').then(module => ({
    default: module.BillingConfigPage,
  }))
);
const AdminUsersRedesignPage = lazy(() =>
  import('./pages/AdminUsersRedesignPage').then(module => ({
    default: module.AdminUsersRedesignPage,
  }))
);
const AdminUserWorkspacePage = lazy(() =>
  import('./pages/AdminUserWorkspacePage').then(module => ({
    default: module.AdminUserWorkspacePage,
  }))
);
const AdminAuthRedesignPage = lazy(() =>
  import('./pages/AdminAuthRedesignPage').then(module => ({
    default: module.AdminAuthRedesignPage,
  }))
);
const TripsPage = lazy(() =>
  import('./pages/TripsPage').then(module => ({
    default: module.TripsPage,
  }))
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then(module => ({
    default: module.SettingsPage,
  }))
);
const NotificationTemplatesPage = lazy(() =>
  import('./pages/NotificationTemplatesPage').then(module => ({
    default: module.NotificationTemplatesPage,
  }))
);
const MarginConfigurationPage = lazy(() =>
  import('./pages/MarginConfigurationPage').then(module => ({
    default: module.MarginConfigurationPage,
  }))
);
const RevenueDashboardPage = lazy(() =>
  import('./pages/RevenueDashboardPage').then(module => ({
    default: module.RevenueDashboardPage,
  }))
);
const BookingProvidersPage = lazy(() =>
  import('./pages/BookingProvidersPage').then(module => ({
    default: module.BookingProvidersPage,
  }))
);
const ViatorOperationsPage = lazy(() =>
  import('./pages/ViatorOperationsPage').then(module => ({
    default: module.ViatorOperationsPage,
  }))
);
const ResolutionReviewPage = lazy(() =>
  import('./pages/ResolutionReviewPage').then(module => ({
    default: module.ResolutionReviewPage,
  }))
);
const SubscriptionsPage = lazy(() =>
  import('./pages/SubscriptionsPage').then(module => ({
    default: module.SubscriptionsPage,
  }))
);
const UserEntitlementsPage = lazy(() =>
  import('./pages/UserEntitlementsPage').then(module => ({
    default: module.UserEntitlementsPage,
  }))
);
const AIQuestionsPage = lazy(() =>
  import('./pages/AIQuestionsPage').then(module => ({
    default: module.AIQuestionsPage,
  }))
);
const ActivityTypesPage = lazy(() =>
  import('./pages/ActivityTypesPage').then(module => ({
    default: module.ActivityTypesPage,
  }))
);
const DestinationsPage = lazy(() =>
  import('./pages/DestinationsPage').then(module => ({
    default: module.DestinationsPage,
  }))
);
const ExploreContentPage = lazy(() =>
  import('./pages/ExploreContentPage').then(module => ({
    default: module.ExploreContentPage,
  }))
);
const SchedulerJobsPage = lazy(() =>
  import('./pages/SchedulerJobsPage').then(module => ({
    default: module.SchedulerJobsPage,
  }))
);
const BudgetsPage = lazy(() =>
  import('./pages/BudgetsPage').then(module => ({
    default: module.BudgetsPage,
  }))
);
const ProviderVendorsPage = lazy(() =>
  import('./pages/ProviderVendorsPage').then(module => ({
    default: module.ProviderVendorsPage,
  }))
);
const SupportTicketsPage = lazy(() =>
  import('./pages/SupportTicketsPage').then(module => ({
    default: module.SupportTicketsPage,
  }))
);
const ReferralInvitesPage = lazy(() =>
  import('./pages/ReferralInvitesPage').then(module => ({
    default: module.ReferralInvitesPage,
  }))
);
const ReferralRewardsPage = lazy(() =>
  import('./pages/ReferralRewardsPage').then(module => ({
    default: module.ReferralRewardsPage,
  }))
);
const TripCollaborationModerationPage = lazy(() =>
  import('./pages/TripCollaborationModerationPage').then(module => ({
    default: module.TripCollaborationModerationPage,
  }))
);
const TripInvitesModerationPage = lazy(() =>
  import('./pages/TripInvitesModerationPage').then(module => ({
    default: module.TripInvitesModerationPage,
  }))
);
const ShareLinkModerationPage = lazy(() =>
  import('./pages/ShareLinkModerationPage').then(module => ({
    default: module.ShareLinkModerationPage,
  }))
);
const ReportedUsersPage = lazy(() =>
  import('./pages/ReportedUsersPage').then(module => ({
    default: module.ReportedUsersPage,
  }))
);
const InviteAbusePage = lazy(() =>
  import('./pages/InviteAbusePage').then(module => ({
    default: module.InviteAbusePage,
  }))
);
const BlockedUserConflictsPage = lazy(() =>
  import('./pages/BlockedUserConflictsPage').then(module => ({
    default: module.BlockedUserConflictsPage,
  }))
);

function RouteFallback() {
  return (
    <div
      className="min-h-screen bg-[var(--amy-bg)] dark:bg-[var(--amy-bg)]"
      aria-hidden
    />
  );
}

export default function App() {
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<AdminAuthRedesignPage />} />
          <Route
            path="/auth/callback"
            element={<Navigate to="/login" replace />}
          />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Protected routes with admin layout */}
          <Route
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <RoleBasedRoute requiredPermission="audit:read">
                  <DashboardPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="users"
              element={
                <RoleBasedRoute requiredPermission="users:read">
                  <AdminUsersRedesignPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="users/:userId"
              element={
                <RoleBasedRoute requiredPermission="users:read">
                  <AdminUserWorkspacePage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="trips"
              element={
                <RoleBasedRoute requiredPermission="trips:read">
                  <TripsPage />
                </RoleBasedRoute>
              }
            />
            {/* Bookings */}
            <Route
              path="margins"
              element={
                <RoleBasedRoute requiredPermission="bookings:read">
                  <MarginConfigurationPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="revenue"
              element={
                <RoleBasedRoute requiredPermission="bookings:read">
                  <RevenueDashboardPage />
                </RoleBasedRoute>
              }
            />
            {FEATURE_FLAGS.adminBookingProvidersV1 && (
              <Route
                path="booking-providers"
                element={
                  <RoleBasedRoute requiredPermission="bookings:read">
                    <BookingProvidersPage />
                  </RoleBasedRoute>
                }
              />
            )}
            <Route
              path="viator-operations"
              element={
                <RoleBasedRoute requiredPermission="bookings:read">
                  <ViatorOperationsPage />
                </RoleBasedRoute>
              }
            />
            {/* Billing */}
            <Route
              path="products"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <ProductsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="products/:productId/plans"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <PlansPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="plans"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <PlansPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="features"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <FeaturesPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="entitlements"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <PlanEntitlementsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="ai-usage-limits"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <AiUsageLimitsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="ai-observability"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <AiObservabilityPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="subscriptions"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <SubscriptionsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="user-entitlements"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <UserEntitlementsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="currencies"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <CurrencyManagementPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="exchange-rates"
              element={
                <RoleBasedRoute requiredPermission="billing:read">
                  <Navigate to="/currencies?tab=exchange-rates" replace />
                </RoleBasedRoute>
              }
            />
            <Route
              path="billing"
              element={
                <RoleBasedRoute requiredPermission="config:read">
                  <BillingConfigPage />
                </RoleBasedRoute>
              }
            />
            {/* Content */}
            <Route
              path="ai-questions"
              element={
                <RoleBasedRoute requiredPermission="content:read">
                  <AIQuestionsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="activity-types"
              element={
                <RoleBasedRoute requiredPermission="content:read">
                  <ActivityTypesPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="destinations"
              element={
                <RoleBasedRoute requiredPermission="content:read">
                  <DestinationsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="explore-content"
              element={
                <RoleBasedRoute requiredPermission="content:read">
                  <ExploreContentPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="resolution-review"
              element={
                <RoleBasedRoute
                  requiredPermissions={['content:read', 'bookings:read']}
                  requireAll
                >
                  <ResolutionReviewPage />
                </RoleBasedRoute>
              }
            />
            {/* System */}
            <Route
              path="audit"
              element={
                <RoleBasedRoute requiredPermission="audit:read">
                  <AuditLogsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="content/email-templates"
              element={
                <RoleBasedRoute requiredPermission="content:read">
                  <NotificationTemplatesPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="notifications"
              element={
                <RoleBasedRoute requiredPermission="content:read">
                  <Navigate to="/content/email-templates" replace />
                </RoleBasedRoute>
              }
            />
            <Route
              path="scheduler"
              element={
                <RoleBasedRoute requiredPermission="config:read">
                  <SchedulerJobsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="budgets"
              element={
                <RoleBasedRoute requiredPermission="config:read">
                  <BudgetsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="providers"
              element={
                <RoleBasedRoute requiredPermission="config:read">
                  <ProviderVendorsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="support"
              element={
                <RoleBasedRoute requiredPermission="support:read">
                  <SupportTicketsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="referral-invites"
              element={
                <RoleBasedRoute requiredPermission="support:read">
                  <ReferralInvitesPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="referral-rewards"
              element={
                <RoleBasedRoute requiredPermission="support:read">
                  <ReferralRewardsPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="system/collaborators"
              element={
                <RoleBasedRoute requiredPermission="support:read">
                  <TripCollaborationModerationPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="system/trip-invites"
              element={
                <RoleBasedRoute requiredPermission="support:read">
                  <TripInvitesModerationPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="system/share-links"
              element={
                <RoleBasedRoute requiredPermission="support:read">
                  <ShareLinkModerationPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="system/reported-users"
              element={
                <RoleBasedRoute requiredPermission="support:read">
                  <ReportedUsersPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="system/invite-abuse"
              element={
                <RoleBasedRoute requiredPermission="support:read">
                  <InviteAbusePage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="system/blocked-user-conflicts"
              element={
                <RoleBasedRoute requiredPermission="support:read">
                  <BlockedUserConflictsPage />
                </RoleBasedRoute>
              }
            />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}
